// Parses inline reference tokens emitted by Coach prompts and turns them into
// internal app links. Token shapes:
//   [grant:<uuid>]          → /opportunities?grant=<uuid>
//   [bill:<bill_id>]        → /legislation?bill=<bill_id>
//   [article:<slug>]        → /insights/<slug>
//   [funder:<slug>]         → /funders/<slug>
// Optional label form: [grant:<id>|My label] uses the label as link text.

export type CoachRefType = "grant" | "bill" | "article" | "funder";

export type CoachRef = {
  type: CoachRefType;
  id: string;
  label?: string;
  href: string;
};

const TOKEN_RE = /\[(grant|bill|article|funder):([^\]\|]+)(?:\|([^\]]+))?\]/g;

export function refToHref(type: CoachRefType, id: string): string {
  switch (type) {
    case "grant":   return `/opportunities?grant=${encodeURIComponent(id)}`;
    case "bill":    return `/legislation?bill=${encodeURIComponent(id)}`;
    case "article": return `/insights/${encodeURIComponent(id)}`;
    case "funder":  return `/funders/${encodeURIComponent(id)}`;
  }
}

// Returns markdown with tokens replaced by `[label](href)` Markdown links so any
// ReactMarkdown renderer in the app shows them as clickable in-app links.
export function resolveRefsInMarkdown(text: string): string {
  if (!text) return text;
  return text.replace(TOKEN_RE, (_m, type, id, label) => {
    const t = type as CoachRefType;
    const href = refToHref(t, id);
    const display = (label || id).toString().trim();
    return `[${display}](${href})`;
  });
}

export function extractRefs(text: string): CoachRef[] {
  if (!text) return [];
  const out: CoachRef[] = [];
  const re = new RegExp(TOKEN_RE.source, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const t = m[1] as CoachRefType;
    out.push({ type: t, id: m[2], label: m[3], href: refToHref(t, m[2]) });
  }
  return out;
}